'use client';

import React from 'react';
import styled from '@emotion/styled';
import { Link } from "@argo/ui-elements";
import { colors, size, typography, breakpoints } from "@argo/principles";
import { activeState, focusState, rgba } from "@argo/utils";
import NavExpander from './NavExpander';
import { navbp, NAV_HEIGHT } from '../utils/constants';
import useAnalyticsClick from '../../../../analytics/react/useAnalyticsClick';
import { GLOBALNAV_KBB_BASE_URL } from '../../mykbb/utils/constants';
import SubNav from './SubNav';
const {
  primary,
  blue
} = colors;
const NavItem = styled('div')`
  ${typography.types.XSMedium}
  color: ${primary.white};
  flex-grow: 0;
  display: inline-grid;
  flex-direction: column;
  position: relative;
  a {
    color: ${primary.white};
    &:hover {
      text-decoration: none;
    }
  }
  > a,
  label {
    padding: ${size.sm}px;
    display: block;
  }
  @media (min-width: ${breakpoints.md}px) {
    display: flex;
  }
  @media (min-width: ${breakpoints.lg}px) {
    > a,
    label {
      padding-left: ${size.sm}px;
      padding-right: ${size.sm}px;
    }
  }
  @media (min-width: ${navbp.extraWideViewport}) {
    > a,
    label {
      display: inline-block;
      padding-top: ${size.md}px;
      padding-bottom: ${size.md}px;
      padding-left: ${size.sm}px;
      padding-right: ${size.sm}px;
    }
    > ul {
      overflow: hidden;
      position: absolute;
      padding: 0;
      max-height: 0;
      top: ${NAV_HEIGHT}px;
      transition: 200ms ease-in;
      transition-property: max-height, padding-bottom, padding-top;
      li a {
        height: 0;
        padding-top: 0;
        padding-bottom: 0;
        overflow: hidden;
        transition: 200ms ease-in;
        transition-property: height, padding-bottom, padding-top;
      }
    }
    &:hover {
      background: ${blue.mutedBlue};
      transition: background 100ms ease-out;
      > ul {
        padding: ${size.sm}px 0;
        max-height: 300px;
        transition: 200ms cubic-bezier(.55, .1, 1, 1);
        transition-property: max-height, padding-bottom, padding-top;
        li a {
          height: ${size.lg}px;
          padding: ${size.xs}px 36px;
          line-height: ${size.lg}px;
          transition: 200ms cubic-bezier(.55, .1, 1, 1);
          transition-property: height, padding-bottom, padding-top;
          &:hover {
            background-color: ${rgba(primary.white, .2)};
            padding-left: 31px;
            border-left: 5px solid ${blue.marineBlue};
          }
        }
      }
    }
    &:last-of-type {
      > ul {
        right: 0;
      }
      > div, a, label {
        padding-right: ${size.md}px;
      }
    }
    &:first-of-type {
      > div, a, label {
        padding-left: ${size.md}px;
      }
    }
    
    > a {
      ${activeState}
      &::before { // to override the default active state's gray background
        background-color: transparent;
      }
    }
    ${focusState}
  }
`;
const NavLinks = ({
  links,
  isDesktopView,
  pageId
}) => {
  const onAnalyticsClick = useAnalyticsClick();
  const triggerAnalytics = ({
    googleTag,
    pixallTag
  }) => {
    return onAnalyticsClick(() => {
      const analyticsData = {
        event: 'analytics_event',
        eventElement: 'global-nav-header',
        eventAction: 'click',
        eventResult: googleTag,
        eventNonInteractive: false,
        eventData: {}
      };
      const pixallData = {
        event: 'pixall',
        eventSource: 'application',
        eventType: 'click',
        feature: 'global-nav-header',
        label: pixallTag,
        dealerCode: 'kbbcox',
        fullURL: window.location.href,
        program: 'DealerDotCom',
        wspCode: 'KBB',
        pageId: pageId
      };
      let result = {
        type: 'click'
      };
      if (googleTag) {
        result.googleAnalytics = {
          props: analyticsData
        };
      }
      if (pixallTag) {
        result.pixall = {
          props: pixallData
        };
      }
      return result;
    });
  };
  return links.map((link, i) => {
    if (isDesktopView) {
      return React.createElement(NavItem, {
        "data-index": i,
        key: i
      }, React.createElement(Link, {
        "data-analytics": link.omnitureTag,
        "data-partner": link.partnerLink,
        "data-tag-id": `PrimaryNavList${i}`,
        "data-index": i,
        type: 'unstyled',
        onClick: triggerAnalytics({
          googleTag: link?.googleTag,
          pixallTag: link?.pixallTag
        }),
        href: `${GLOBALNAV_KBB_BASE_URL}${link.url}`
      }, link.name), link.dropdownItems && link.dropdownItems.length > 0 && React.createElement(SubNav, {
        menuIndex: i,
        menuItems: link.dropdownItems,
        triggerAnalytics: triggerAnalytics
      }));
    } else {
      if (link.dropdownItems && link.dropdownItems.length > 0) {
        return React.createElement(NavItem, {
          "data-index": i,
          key: i
        }, React.createElement(NavExpander, {
          link: link
        }, React.createElement(SubNav, {
          menuIndex: i,
          menuItems: link.dropdownItems,
          triggerAnalytics: triggerAnalytics
        })));
      } else {
        return React.createElement(NavItem, {
          "data-index": i,
          key: i
        }, React.createElement(Link, {
          "data-analytics": link.omnitureTag,
          "data-partner": link.partnerLink,
          "data-tag-id": `PrimaryNavList${i}`,
          "data-index": i,
          type: 'unstyled',
          href: `${GLOBALNAV_KBB_BASE_URL}${link.url}`
        }, link.name));
      }
    }
  });
};
export default React.memo(NavLinks, ({
  links
}) => {
  return links && links.length;
});